'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { Resolution } from '@blockscout/stats-types';

import useChartQuery from 'src/hooks/useChartQuery';
import ChartWidget from 'src/components/chart/ChartWidget';

import styles from 'src/components/chart/ChartWidget.module.css';

const DailyTransactionsChart: React.FC = () => {
  const [ txCount, setTxCount ] = useState<string | null>(null);
  const [ ogpuPrice, setOgpuPrice ] = useState<string | null>(null);

  const { items, lineQuery } = useChartQuery('newTxns', Resolution.DAY, 'oneYear', true);

  const last15Days = useMemo(() => {
    if (!items) return items;
    return items.slice(-15);
  }, [ items ]);

  useEffect(() => {
    fetch(`${process.env.NEXT_PUBLIC_STATS_API_HOST}/api/v1/counters`)
      .then((res) => res.json())
      .then((data) => {
        const txCounter = data?.counters?.find((c: { id: string }) => c.id === 'totalTxns');
        if (txCounter) {
          setTxCount(Number(txCounter.value).toLocaleString());
        }
      })
      .catch(() => {});

    const urls = [
      '/backend/ogpu-price',
      'https://api.coingecko.com/api/v3/simple/price?ids=open-gpu&vs_currencies=usd',
    ];
    const fetchPrice = async() => {
      for (const url of urls) {
        try {
          const res = await fetch(url);
          const data = await res.json();
          if (data?.price != null) {
            setOgpuPrice(Number(data.price).toFixed(4));
            return;
          }
          if (data?.['open-gpu']?.usd != null) {
            setOgpuPrice(Number(data['open-gpu'].usd).toFixed(4));
            return;
          }
        } catch {
          // try next URL
        }
      }
    };
    fetchPrice();
  }, []);

  const headerExtra = (
    <>
      { txCount && (
        <span className={ styles.badge }>Txns: { txCount }</span>
      ) }
      { ogpuPrice && (
        <span className={ `${ styles.badge } ${ styles.badgeGreen }` }>OGPU: ${ ogpuPrice }</span>
      ) }
    </>
  );

  return (
    <ChartWidget
      items={ last15Days }
      title="Daily transactions"
      description="Number of new transactions per day"
      isLoading={ lineQuery.isPlaceholderData || lineQuery.isLoading }
      isError={ lineQuery.isError }
      headerExtra={ headerExtra }
    />
  );
};

export default DailyTransactionsChart;
